import type { ControllerGroup, GroupedPort, PortReference } from '@/features/resource/utils/portGroups'

/*
 * Geometry for the graph view of the ports section.
 *
 * The two columns are fixed rather than solved for: a control port always sits at one x and the
 * metering port measuring it at another, so the only thing an edge can ever be seen to cross is a
 * band boundary. That is the whole argument for the view, and a force-directed layout would give it
 * away in exchange for prettier hairballs.
 */

/** Wide enough for a port name plus its type on a second line, at the sizes the nodes are drawn. */
export const NODE_WIDTH = 180
export const NODE_HEIGHT = 52

/** The band's own box. Nodes sit inside it; the controller's label sits in the gutter to its left. */
export const BAND_X = 168
export const BAND_WIDTH = 536

/** The left edge of the gutter's clip, keeping a label off the very edge of the stage. */
export const GUTTER_LABEL_INSET = 4

/** Right edge of the gutter, less a little air. Labels are end-anchored here. */
export const GUTTER_LABEL_X = 148

/** The width a gutter label has before the clip cuts it, and what the truncation measures against. */
export const GUTTER_LABEL_WIDTH = GUTTER_LABEL_X - GUTTER_LABEL_INSET

export const CONTROL_X = 184
export const METERING_X = 512
export const VIEW_WIDTH = 820

const ROW_GAP = 16
const BAND_PADDING = 12

/** The space between two bands. The cross-controller tag sits in it, so it has to stay taller. */
const BAND_GAP = 32

export const CROSS_TAG_WIDTH = 116
export const CROSS_TAG_HEIGHT = 18

/**
 * The width the tag's text is *made* to occupy, rather than the width it happens to want.
 *
 * The badge is a fixed 116px, so the text has to fit whatever font `monospace` resolves to. One
 * platform measured 109.4px, but that rests on a 0.602 advance ratio the code never states and a
 * 0.65 ratio would overflow. Given as an SVG `textLength` with `lengthAdjust="spacingAndGlyphs"`,
 * the width is a guarantee the renderer honours rather than a calculation that happens to hold.
 */
export const CROSS_TAG_TEXT_WIDTH = CROSS_TAG_WIDTH - 16

/**
 * How close the cross-controller tag may come to either column of nodes. The columns are 148px
 * apart and the tag is 116px, so this takes half the 32px left over and leaves the tag 16px of
 * travel — narrow on purpose, so the tag lands in nearly the same place wherever the curve crosses.
 */
const TAG_COLUMN_CLEARANCE = 8

/**
 * The floor on SVG text sizes in this view.
 *
 * SVG text is measured in user units scaled by the container, not by the reader's font preference,
 * so the stage's text does not grow when someone sets a larger default (WCAG 1.4.4) — and at the
 * stage's minimum width the scale is 0.854, so a nominal 9 rendered at about 7.7px. This floor does
 * not make the stage honour a font preference; that needs the text out of the SVG or the whole
 * stage sized in ems. It stops the smallest labels starting below legibility before scaling. Where
 * a size went up, tracking came down to keep the box it sits in the same width, which is what the
 * small labels' `letterSpacing` values are set against.
 */
export const MIN_SVG_FONT_SIZE = 11

/** How far a curve bows horizontally, as a fraction of the gap it spans. */
const CURVE_BOW = 0.55

/**
 * Samples taken along a crossing edge when looking for where it enters the gap. A cubic has no
 * closed form for "the t at which y is this", and over the ~150px an edge spans these put the tag
 * inside a pixel of the boundary.
 */
const CURVE_SAMPLES = 200

export type PortGraphNode = {
  port: GroupedPort
  x: number
  y: number
  width: number
  height: number
}

export type PortGraphBand = {
  group: ControllerGroup
  x: number
  y: number
  width: number
  height: number
  nodes: PortGraphNode[]
}

export type PortGraphEdge = {
  fromPortId: string
  toPortId: string
  /** Resolved by getPortReferences, not here — including whether it leaves the band. */
  reference: PortReference
  path: string
  /**
   * Where the "cross-controller" tag goes, or null when the relation does not need one.
   *
   * Computed from the two bands rather than placed at a chosen point on the curve, so it stays on
   * the boundary being crossed when the layout changes. A tag pinned to, say, the curve's midpoint
   * would drift off the boundary the moment a band gained a port.
   *
   * Null on a relation that stays inside one band, and on every crossing after the first to leave
   * by the same boundary — including crossings bound for different bands, which leave by the same
   * edge of the one they start in. Tags are 116px wide and land within a 16px window of each other,
   * so a second is drawn almost on top of the first; one tag says "relations leave here" for all of
   * them, and the edges stay individually drawn.
   */
  crossingTag: { x: number; y: number } | null
  /**
   * The height at which this edge leaves its band, or null when it stays inside one. Carried by
   * every crossing edge rather than only the one holding the tag, because the tag belongs to the
   * boundary and anything asking whether that boundary is still in play has to ask all of them.
   */
  crossingBoundary: number | null
}

export type PortGraph = {
  bands: PortGraphBand[]
  nodes: PortGraphNode[]
  edges: PortGraphEdge[]
  width: number
  height: number
}

type Curve = {
  x1: number
  y1: number
  cx1: number
  cy1: number
  cx2: number
  cy2: number
  x2: number
  y2: number
}

function measurementCurve(from: PortGraphNode, to: PortGraphNode): Curve {
  const x1 = from.x + from.width
  const y1 = from.y + from.height / 2
  const x2 = to.x
  const y2 = to.y + to.height / 2
  const bow = (x2 - x1) * CURVE_BOW

  return { x1, y1, cx1: x1 + bow, cy1: y1, cx2: x2 - bow, cy2: y2, x2, y2 }
}

function curveToPath(curve: Curve): string {
  return `M ${curve.x1} ${curve.y1} C ${curve.cx1} ${curve.cy1}, ${curve.cx2} ${curve.cy2}, ${curve.x2} ${curve.y2}`
}

function pointOnCurve(curve: Curve, t: number): { x: number; y: number } {
  const m = 1 - t

  return {
    x: m ** 3 * curve.x1 + 3 * m ** 2 * t * curve.cx1 + 3 * m * t ** 2 * curve.cx2 + t ** 3 * curve.x2,
    y: m ** 3 * curve.y1 + 3 * m ** 2 * t * curve.cy1 + 3 * m * t ** 2 * curve.cy2 + t ** 3 * curve.y2,
  }
}

/**
 * The height at which a crossing edge leaves the band it starts in: the gap on the source band's own
 * edge, not the midpoint between the two bands. Those coincide only when the bands are adjacent —
 * with a relation that skips a band the midpoint falls inside the band between, and the tag lands
 * on that band's edges claiming *its* relation is the one leaving. This is the whole of what a
 * tag's position depends on, so it is also what decides whether a second tag would overlap a first.
 */
function crossingBoundaryY(curve: Curve, fromBand?: PortGraphBand, toBand?: PortGraphBand): number {
  if (fromBand === undefined || toBand === undefined) {
    return curve.y2
  }

  return toBand.y > fromBand.y ? fromBand.y + fromBand.height + BAND_GAP / 2 : fromBand.y - BAND_GAP / 2
}

/**
 * Where on that boundary the tag sits.
 *
 * Clamped clear of both columns, because nothing places the tag in the gap between them — it lands
 * wherever the curve happens to be at the boundary. The steeper the edge the nearer the source node
 * it crosses, so a control port measured from low in a taller adjacent band puts the tag on its own
 * node: 10px of overlap with four metering ports in that band, 24px with ten.
 *
 * The clamp trades the crossing *point* for the boundary *height*, and the height is the half that
 * carries the meaning — the tag is a statement about the boundary, not about where a Bézier crossed
 * it. The window is narrow enough that a clamped tag still reads as deliberately placed.
 */
function crossingTagPoint(curve: Curve, boundaryY: number): { x: number; y: number } {
  // Seeded from the first sample: the loop covers 0..1 inclusive and overwrites any separate seed.
  let best = pointOnCurve(curve, 0)
  let bestGap = Math.abs(best.y - boundaryY)

  for (let step = 0; step <= CURVE_SAMPLES; step += 1) {
    const point = pointOnCurve(curve, step / CURVE_SAMPLES)
    const gap = Math.abs(point.y - boundaryY)
    if (gap < bestGap) {
      bestGap = gap
      best = point
    }
  }

  const minX = CONTROL_X + NODE_WIDTH + TAG_COLUMN_CLEARANCE + CROSS_TAG_WIDTH / 2
  const maxX = METERING_X - TAG_COLUMN_CLEARANCE - CROSS_TAG_WIDTH / 2

  return { x: Math.min(Math.max(best.x, minX), maxX), y: best.y }
}

/**
 * Lays out the ports of one resource as a graph: ports are nodes, the measurement relation is an
 * edge, and each controller is a band containing its own ports.
 *
 * Derives nothing about the data — the grouping and the relations, including which of them leave
 * their controller, arrive already resolved. What is added here is geometry. Bands keep the order
 * the groups arrive in, which puts the unidentified group last, exactly as the table stacks it.
 */
export function buildPortGraph(
  groups: readonly ControllerGroup[],
  referencesByPortId: ReadonlyMap<string, PortReference[]>,
): PortGraph {
  const bands: PortGraphBand[] = []
  let top = BAND_GAP / 2

  for (const group of groups) {
    const controlPorts = group.ports.filter((port) => port.kind === 'CONTROL')
    const meteringPorts = group.ports.filter((port) => port.kind === 'METERING')

    // A band is as tall as its fuller column. The floor of one keeps a band that somehow holds no
    // ports from collapsing to a line, which would read as a rendering fault rather than as a
    // controller with nothing on it.
    const rows = Math.max(controlPorts.length, meteringPorts.length, 1)
    const height = BAND_PADDING * 2 + rows * NODE_HEIGHT + (rows - 1) * ROW_GAP

    // Columns are centred rather than top-aligned, so one metering port beside three control ports
    // is drawn next to the middle of them instead of dragging every edge up to the first row.
    const bandTop = top
    const column = (ports: GroupedPort[], x: number): PortGraphNode[] => {
      const columnTop = bandTop + BAND_PADDING + ((rows - ports.length) * (NODE_HEIGHT + ROW_GAP)) / 2

      return ports.map((port, index) => ({
        port,
        x,
        y: columnTop + index * (NODE_HEIGHT + ROW_GAP),
        width: NODE_WIDTH,
        height: NODE_HEIGHT,
      }))
    }

    bands.push({
      group,
      x: BAND_X,
      y: bandTop,
      width: BAND_WIDTH,
      height,
      nodes: [...column(controlPorts, CONTROL_X), ...column(meteringPorts, METERING_X)],
    })

    top += height + BAND_GAP
  }

  const nodes = bands.flatMap((band) => band.nodes)
  /*
   * Control and metering ids share one keyspace here, as they do in `referencesByPortId`. Both are
   * UUIDs from different tables, so a collision is not a case to design around — but it is worth
   * naming, because the consequence is quiet: a relation resolves to the wrong node and draws an
   * edge between two ports that are not related. A wrong picture rather than a broken one.
   */
  const nodeByPortId = new Map(nodes.map((node) => [node.port.id, node] as const))
  const bandByPortId = new Map(bands.flatMap((band) => band.nodes.map((node) => [node.port.id, band] as const)))

  const edges: PortGraphEdge[] = []
  const taggedBoundaries = new Set<number>()

  for (const node of nodes) {
    // Read from the control side only. getPortReferences reports each relation from both of its
    // ends, so walking every port would draw every edge twice, on top of itself.
    if (node.port.kind !== 'CONTROL') {
      continue
    }

    for (const reference of referencesByPortId.get(node.port.id) ?? []) {
      const target = nodeByPortId.get(reference.targetPortId)

      // No node to draw to. Still reported in the table, the panel and the node's spoken name.
      if (target === undefined) {
        continue
      }

      const curve = measurementCurve(node, target)

      /*
       * One tag per boundary, keeping the first crossing in node order. Keyed on the boundary rather
       * than on the pair of bands, since the boundary is what the tag's position derives from: two
       * relations leaving one band for two different bands land on the same edge of it, within a
       * 16px window, and the second is drawn almost on top of the first. The edges are untouched.
       */
      const boundaryY = reference.isCrossController
        ? crossingBoundaryY(curve, bandByPortId.get(node.port.id), bandByPortId.get(target.port.id))
        : null
      const boundaryIsTagged = boundaryY !== null && taggedBoundaries.has(boundaryY)

      if (boundaryY !== null) {
        taggedBoundaries.add(boundaryY)
      }

      edges.push({
        fromPortId: node.port.id,
        toPortId: target.port.id,
        reference,
        path: curveToPath(curve),
        crossingTag: boundaryY !== null && !boundaryIsTagged ? crossingTagPoint(curve, boundaryY) : null,
        crossingBoundary: boundaryY,
      })
    }
  }

  return {
    bands,
    nodes,
    edges,
    width: VIEW_WIDTH,
    // Half a gap of air below the last band, matching the half-gap above the first.
    height: Math.max(top - BAND_GAP / 2, 0),
  }
}

/** The ports an edge touches, for isolating one port's relations from the rest. */
export function relatedPortIds(portId: string, edges: readonly PortGraphEdge[]): Set<string> {
  const related = new Set<string>([portId])

  for (const edge of edges) {
    if (edge.fromPortId === portId) {
      related.add(edge.toPortId)
    }
    if (edge.toPortId === portId) {
      related.add(edge.fromPortId)
    }
  }

  return related
}
